import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';

import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { Company } from '_lib/api/apiTypes.shim';
import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { NoteList } from '_lib/featureComponents/NoteList/NoteList';
import { ReadOnlyContact } from '_lib/featureComponents/ReadOnlyContact/ReadOnlyContact';
import { sortedNotes } from '_lib/utils/helpers';

const LiveDataMessage = 'Live data - not saved on call record' as const;

export const PhoneCallStCustomerDetail = (props: {
  providerCustomerId: string;
  companyData: Company;
  membershipDisplay: React.ReactNode;
}) => {
  const { providerCustomerId, companyData, membershipDisplay } = props;

  const customerDetail = useContext(ApiContext)
    .serviceTitan(companyData.id)
    .customers.useGetById({ id: providerCustomerId });

  if (customerDetail.error) {
    return (
      <ManualErrorBoundary
        errorState={{ error: customerDetail.error, message: 'Error fetching customer details' }}
        reportingCtx={{ extra: { providerCustomerId, companyData } }}
        onRefresh={async () => {
          customerDetail.refetch();
        }}
      />
    );
  }

  if (customerDetail.isFetching)
    return (
      <FlexBox sx={{ position: 'relative', minHeight: '150px' }}>
        <BackdropSpinner open />
      </FlexBox>
    );

  if (!customerDetail.isFetching && !customerDetail.data) return <Typography text='No customer details found' />;

  return (
    <>
      <HeaderBox headerText='Contacts' liveIconText={LiveDataMessage}>
        <Stack direction='row' spacing={0} sx={{ flexWrap: 'wrap' }}>
          {customerDetail.data?.contacts.map((contact) => (
            <ReadOnlyContact contact={contact} key={contact.providerId} />
          ))}
        </Stack>
      </HeaderBox>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='body1Bold' text='Memberships' />
        </AccordionSummary>
        <AccordionDetails>{membershipDisplay}</AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <Typography variant='body1Bold' text='Notes' />
            <Tooltip title={LiveDataMessage} placement={'top-end'}>
              <OfflineBoltIcon fontSize='small' color='primary' />
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <NoteList
            notes={sortedNotes(customerDetail.data?.notes || [])}
            timezone={companyData.timezone}
            displayShowMore
            displayAsToday
            divider
            disablePadding
            sx={(theme) => ({
              marginTop: 1,
              border: 1,
              borderRadius: 2,
              borderColor: theme.palette.divider
            })}
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
};
